import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Search } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { useClickAway } from 'react-use';

import { InfoIcon } from '#/assets/svgs';
import { Input, Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '#/components/ui';
import { RequestCompanyDialog } from '#/pages/home';
import { useInfiniteScrollQuery, queryKeys } from '#/services';
import { cn, debounce } from '#/utils';

const AutoCompleteInput = ({
    label,
    placeholder,
    value,
    setSearch,
    debounceDelay = 500,
    queryKey,
    searchIcon,
    tooltip,
    fetch_url,
    addCompany = false,
}) => {
    const [searchTerm, setSearchTerm] = useState(value?.[queryKey] || '');
    const [queryParams, setQueryParams] = useState({});

    const [showDropdown, setShowDropdown] = useState(false);

    const { ref, inView } = useInView();
    const modalClickedRef = useRef(false);
    const containerRef = useRef(null);

    useClickAway(containerRef, () => {
        if (!modalClickedRef.current) {
            setShowDropdown(false);
        }
    });

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteScrollQuery({
        key: [queryKeys.AUTOCOMPLETE, queryKey, searchTerm],
        url: fetch_url,
        params: queryParams,
    });

    const options = data?.pages?.flatMap(page => page.results) || [];

    const debouncedSearch = useRef(
        debounce(val => {
            setQueryParams({ name: val });
        }, debounceDelay)
    ).current;

    const handleSearchChange = useCallback(
        e => {
            const val = e.target.value;
            setSearchTerm(val);
            debouncedSearch(val);
        },
        [debouncedSearch]
    );

    const handleFocus = useCallback(() => {
        setShowDropdown(true);
    }, []);

    const handleSelect = useCallback(
        option => {
            setSearch(prev => ({
                ...prev,
                [queryKey]: option.name || `${option.firstName} ${option.lastName}`,
            }));
            setSearchTerm(option.name || option.firstName);
            setShowDropdown(false);
        },
        [queryKey, setSearch]
    );

    const renderOption = useCallback(
        (option, idx) => {
            const handleMouseDown = () => handleSelect(option);
            return (
                <div key={idx} onMouseDown={handleMouseDown} className='px-3 py-2 hover:bg-gray-100 cursor-pointer'>
                    {option.name || `${option.firstName} ${option.lastName}`}
                </div>
            );
        },
        [handleSelect]
    );

    const handleModalMouseDown = useCallback(() => {
        modalClickedRef.current = true;
        setTimeout(() => {
            modalClickedRef.current = false;
        }, 0);
    }, []);

    useEffect(() => {
        if (inView && hasNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, fetchNextPage]);

    useEffect(() => {
        if (value !== searchTerm) {
            setSearchTerm(value || '');
        }
    }, [value]);

    useEffect(() => {
        if (searchTerm?.length === 0) {
            setQueryParams({});
        }
    }, [searchTerm]);

    return (
        <div ref={containerRef} className='w-52 relative'>
            {searchIcon && !searchTerm && (
                <div className='absolute left-1 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none'>
                    <Search className='w-4 h-4' />
                </div>
            )}

            <Input
                name={queryKey}
                placeholder={placeholder || `Search by ${label}`}
                value={searchTerm}
                autoComplete='off'
                onChange={handleSearchChange}
                onFocus={handleFocus}
                className={cn(
                    'w-full placeholder-gray400 font-inter !text-sm font-normal',
                    searchIcon ? 'pl-5' : 'pl-3',
                    tooltip ? 'pr-8' : 'pr-3'
                )}
            />

            {tooltip && (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className='absolute inset-y-0 right-2 flex items-center pointer-events-auto'>
                                <InfoIcon className='w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer' />
                            </div>
                        </TooltipTrigger>
                        <TooltipContent side='top' align='end' className='bg-white'>
                            {tooltip}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}

            {showDropdown && (
                <div className='absolute z-50 bg-white border border-gray-200 mt-1 w-full rounded-md shadow-sm max-h-64 overflow-auto text-sm'>
                    {isLoading ? (
                        <div className='px-3 py-2 text-gray500'>Loading...</div>
                    ) : options?.length > 0 ? (
                        <>
                            {options?.map(renderOption)}
                            {hasNextPage && (
                                <div ref={ref} className='py-2 text-center text-gray-400'>
                                    {isFetchingNextPage ? 'Loading more…' : ''}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className='flex flex-col items-center justify-center text-center px-6 py-6 '>
                            <Search className='w-10 h-10 p-2 bg-gray100 rounded-full text-textPrimary' />
                            <div className='text-sm font-semibold text-gray800 pt-4'>Not Found!</div>
                            <div className='text-xs text-gray500 pt-1 max-w-[210px] break-words'>
                                There is no result for "<span className='font-medium break-all'>{searchTerm}</span>" you
                                searched.
                            </div>
                            {addCompany && (
                                <div className='text-xs text-gray500 space-y-4' onMouseDown={handleModalMouseDown}>
                                    <p className='text-xs text-gray500 pt-1'>Would you like to add this to THE LIST?</p>
                                    <RequestCompanyDialog />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export { AutoCompleteInput };
